import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Inbox as InboxIcon,
  Mail,
  CheckCircle2,
  AlertTriangle,
  Clock,
  FileX2,
  CalendarX2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { api } from "../lib/api";
import { formatRelativeTime, notificationTitle } from "../lib/notifications";

const KIND_ICON = {
  invite_received: Mail,
  task_assigned: InboxIcon,
  task_overdue: Clock,
  workflow_completed: CheckCircle2,
  workflow_errored: AlertTriangle,
  document_declined: FileX2,
  document_expired: CalendarX2,
};

const KIND_COLOR = {
  invite_received: "text-blue-600 bg-blue-50",
  task_assigned: "text-indigo-600 bg-indigo-50",
  task_overdue: "text-amber-700 bg-amber-50",
  workflow_completed: "text-emerald-700 bg-emerald-50",
  workflow_errored: "text-rose-700 bg-rose-50",
  document_declined: "text-rose-700 bg-rose-50",
  document_expired: "text-orange-700 bg-orange-50",
};

function NotificationItem({ n, onClick }) {
  const Icon = KIND_ICON[n.kind] || Bell;
  const color = KIND_COLOR[n.kind] || "text-zinc-700 bg-zinc-100";
  const unread = !n.read_at;
  return (
    <button
      type="button"
      onClick={() => onClick(n)}
      className={`w-full text-left flex gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors ${
        unread ? "bg-blue-50/30" : ""
      }`}
      data-testid={`notif-item-${n.id}`}
    >
      <span
        className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${color}`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div
            className={`text-sm truncate ${
              unread ? "font-medium text-zinc-900" : "text-zinc-700"
            }`}
          >
            {notificationTitle(n)}
          </div>
          {unread && (
            <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1" />
          )}
        </div>
        {n.payload?.title && (
          <div className="text-xs text-zinc-500 truncate mt-0.5">
            {n.payload.title}
          </div>
        )}
        <div className="text-[11px] text-zinc-400 mt-0.5">
          {formatRelativeTime(n.created_at)}
        </div>
      </div>
    </button>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const countQuery = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => api.countNotifications(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const listQuery = useQuery({
    queryKey: ["notifications", "list", "header"],
    queryFn: () => api.listNotifications({ limit: 20 }),
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: (id) => api.markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const onItemClick = (n) => {
    if (!n.read_at) markReadMutation.mutate(n.id);
    setOpen(false);
    // Navigate to a kind-appropriate destination
    const url = navigateUrlForNotification(n);
    if (url) navigate(url);
  };

  const unread = countQuery.data?.unread || 0;
  const items = listQuery.data?.items || [];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        data-testid="notification-bell"
        className="relative h-9 w-9 rounded-full hover:bg-zinc-900/5 transition-colors flex items-center justify-center text-zinc-700"
        aria-label="Уведомления"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0 max-h-[480px] flex flex-col">
        <div className="px-4 py-3 border-b border-zinc-900/5 flex items-center justify-between">
          <div className="font-medium text-sm text-zinc-900">Уведомления</div>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markAllMutation.mutate()}
              className="text-xs text-primary hover:underline"
              data-testid="notif-mark-all"
            >
              Прочитать все
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {listQuery.isLoading && (
            <div className="px-4 py-6 text-sm text-zinc-500 text-center">
              Загрузка…
            </div>
          )}
          {!listQuery.isLoading && items.length === 0 && (
            <div className="px-4 py-10 text-sm text-zinc-500 text-center">
              Нет уведомлений
            </div>
          )}
          {items.map((n) => (
            <NotificationItem key={n.id} n={n} onClick={onItemClick} />
          ))}
        </div>
        <div className="border-t border-zinc-900/5 px-4 py-2 text-center">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
            className="text-xs text-primary hover:underline"
          >
            Вся история
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function navigateUrlForNotification(n) {
  const p = n.payload || {};
  if (p.task_url) return p.task_url;
  if (p.instance_url) return p.instance_url;
  if (p.accept_url) return p.accept_url;
  if (p.document_url) return p.document_url;
  if (n.kind === "invite_received") return "/team";
  if (n.kind === "task_assigned" || n.kind === "task_overdue") return "/inbox";
  if (n.kind === "workflow_completed" || n.kind === "workflow_errored")
    return "/workflows";
  if (n.kind === "document_declined" || n.kind === "document_expired")
    return p.document_id ? `/documents/${p.document_id}` : "/documents";
  return null;
}
